import { baseApiAuthenticated } from '@/api/v2/index';
import { Customer } from '@/models/customer';
import { buildSearchParams, urlBuilder } from '@/api/utils';
import { transformCustomerFromResponse } from '@/models/customer';
import { BackendType, FrontendType, ObjectType } from '@/models/general';

const baseUrl = urlBuilder('auth', 'customers');

export type GetCustomerSearchParams = {
    size?: number;
    page?: number;
    sort?: string;
    search?: string;
};

type GetCustomerResponse<T extends ObjectType = FrontendType> = {
    content: Customer<T>[];
    size: number;
    totalElements: number;
    totalPages: number;
};

export const customerApi = baseApiAuthenticated
    .enhanceEndpoints({ addTagTypes: ['CUSTOMERS'] })
    .injectEndpoints({
        endpoints: (builder) => ({
            getCustomer: builder.query<Customer, { customerId: string }>({
                query: ({ customerId }) =>
                    `${baseUrl}${buildSearchParams({ search: customerId })}`,

                transformResponse(response: GetCustomerResponse<BackendType>) {
                    return {
                        ...transformCustomerFromResponse(response.content[0]),
                    };
                },
                providesTags: ['CUSTOMERS'],
            }),
            getCustomers: builder.query<
                GetCustomerResponse,
                GetCustomerSearchParams | null
            >({
                query: (searchParams: GetCustomerSearchParams) =>
                    `${baseUrl}${buildSearchParams(searchParams)}`,

                transformResponse(response: GetCustomerResponse<BackendType>) {
                    return {
                        ...response,
                        content: response.content.map(
                            transformCustomerFromResponse
                        ),
                    };
                },
                providesTags: ['CUSTOMERS'],
            }),

            putCustomer: builder.mutation<Customer, Customer>({
                query: (customer: Customer) => ({
                    url: `${baseUrl}/${customer.id}`,
                    method: 'PUT',
                    body: customer,
                }),
                invalidatesTags: ['CUSTOMERS'],
            }),

            deleteCustomer: builder.mutation<void, { id: string }>({
                query: ({ id }) => ({
                    url: `${baseUrl}/${id}`,
                    method: 'DELETE',
                    body: {
                        id,
                    },
                }),

                invalidatesTags: ['CUSTOMERS'],
            }),
        }),
    });

export const {
    useGetCustomersQuery,
    usePutCustomerMutation,
    useGetCustomerQuery,
    useDeleteCustomerMutation,
} = customerApi;
